import { refreshAccessToken, getCurrentAccessToken } from "./apiService";

const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL;

let wsRef = null;
let wsInitPayloadRef = null;
let pingIntervalRef = null;

// Callbacks
let callbacks = {
  setStatus: null,
  setLiveCallActive: null,
  onTranscriptMessage: null, // (messageData, callIdParam) => void
  onSuggestionsMessage: null, // (messageData, callIdParam) => void — AI interview guidance
  onWsOpen: null,            // () => void — fires when socket.onopen completes
};

export function initWsService(cbs) {
  callbacks = { ...callbacks, ...cbs };
}

export function connectWebSocket(callIdParam, initPayload) {
  if (wsRef && wsRef.readyState === 1) {
    return;
  }

  try {
    if (callbacks.setStatus) callbacks.setStatus("Connecting to WebSocket...");

    const socket = new WebSocket(WEBSOCKET_URL);
    wsRef = socket;

    socket.onopen = () => {
      if (callbacks.setStatus) callbacks.setStatus("Connected to WebSocket");
      if (callbacks.setLiveCallActive) callbacks.setLiveCallActive(true);
      if (callbacks.onWsOpen) callbacks.onWsOpen();

      // Send initial payload
      let payload = initPayload;
      if (!payload && callIdParam && callIdParam.trim()) {
        payload = { action: "getTranscript", callId: callIdParam };
      }

      // Ensure we include the current access token (if present) — avoid sending empty token
      try {
        const currentAccess = getCurrentAccessToken();
        if (payload && (!payload.token || payload.token === "") && currentAccess) {
          payload = { ...payload, token: currentAccess };
        }
      } catch (err) {
        // ignore
      }

      if (payload) {
        wsInitPayloadRef = payload;
        socket.send(JSON.stringify(payload));
      }

      // Ping every 5 minutes
      pingIntervalRef = setInterval(() => {
        if (socket.readyState === 1) {
          socket.send(JSON.stringify({ action: "ping" }));
        }
      }, 5 * 60 * 1000);
    };

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.message === "Pong - connection alive") return;

        // Handle auth errors
        if (
          message?.status === 401 ||
          message?.statusCode === 401 ||
          message?.errorCode === "401" ||
          message?.error === "Unauthorized"
        ) {
          (async () => {
            const newToken = await refreshAccessToken();
            if (!newToken) return;
            if (!wsRef || wsRef.readyState !== 1) return;
            if (!wsInitPayloadRef) return;
            const newPayload = { ...wsInitPayloadRef, token: newToken };
            wsInitPayloadRef = newPayload;
            wsRef.send(JSON.stringify(newPayload));
          })();
          return;
        }

        // AI interview guidance. Rides alongside the transcript for the same
        // chunk, but is a FULL REPLACEMENT rather than an append, so it gets its
        // own path. The return keeps it out of the transcript branch below, which
        // would otherwise turn a message carrying no `transcript` key into
        // "No transcript available".
        if (message.action === "newSuggestions") {
          if (callbacks.onSuggestionsMessage) {
            callbacks.onSuggestionsMessage(message.data || message, callIdParam);
          }
          return;
        }

        // Handle transcript messages
        if (
          message.action === "newTranscript" ||
          message.action === "getTranscript" ||
          message.action === "transcript"
        ) {
          if (callbacks.onTranscriptMessage) {
            callbacks.onTranscriptMessage(message.data || message, callIdParam);
          }
        }
      } catch (err) {
        // ignore parse errors
      }
    };

    socket.onerror = (err) => {
      if (callbacks.setStatus) callbacks.setStatus("WebSocket error");
    };

    socket.onclose = (ev) => {
      if (callbacks.setStatus) callbacks.setStatus("Disconnected from WebSocket");
      if (callbacks.setLiveCallActive) callbacks.setLiveCallActive(false);
      if (pingIntervalRef) clearInterval(pingIntervalRef);
    };
  } catch (err) {
    // ignore connection errors
  }
}

export function closeWebSocket() {
  try {
    if (wsRef) wsRef.close();
  } catch {}
  if (pingIntervalRef) clearInterval(pingIntervalRef);
}
